import { CompanySettings, Model, ModelRow, Navigation, ObjectUtil, StringUtil } from "@mcleod/core";
import { ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, ForcedCase, Image, Label, LookupModelSelectionEvent, Panel, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { getDispatchControlValue, LMEGeneralSettings } from "@mcleod/general";
import { AutogenLayoutMovementSearch } from "./autogen/AutogenLayoutMovementSearch";
import { ImageName } from "@mcleod/images";
import { LabelStyles } from "@mcleod/components/src/components/label/LabelStyles";
import { OrderTabView } from "./OrderTabView";
import { CommonTooltips } from "@mcleod/common";
import { PlanningUtil } from "./planning/PlanningUtil";

export class MovementSearch extends AutogenLayoutMovementSearch {
    onLoad() {
        this.tableMovements.overrideUrlParams = (selectedRow: TableRow) => {
            const orderId = !selectedRow.data.isNull("orders.id") ? selectedRow.data.get("orders.id"): "none";
            return { key: selectedRow.data.get("id"), orderId: orderId, showVoid: true }
        }
        this.tableMovements.doOnResolveRowEdit = async (updatedRow: ModelRow) => {
            return await this.lookupRow(updatedRow);
        }
        const partialShipmentEnabled = getDispatchControlValue("enable_manifesting");
        if (partialShipmentEnabled === "Y") {
            this.textboxOrdersId.caption = "Order/Manifest ID"
        }
        this.switchLoaded.allowIndeterminate = true;
        this.switchLoaded.checked = undefined;
        this.switchAuthorized.allowIndeterminate = true;
        this.switchAuthorized.checked = undefined;
        this.switchBrokerage.allowIndeterminate = true;
        this.switchBrokerage.checked = undefined;

        this.textboxOriginLocation.addLookupModelSelectionListener((event: LookupModelSelectionEvent) =>
            this.searchStopSelection("origin", this.panelShipperLocation, event));
        this.textboxDestLocation.addLookupModelSelectionListener((event: LookupModelSelectionEvent) =>
            this.searchStopSelection("dest", this.panelConsigneeLocation, event)
        );

        this.textboxStopOriginLocationId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) =>
            this.searchStopPopulateSelection("origin", this.panelShipperLocation, event));
        this.textboxStopDestLocationId.addLookupModelSelectionListener(event =>
            this.searchStopPopulateSelection("dest", this.panelConsigneeLocation, event)
        );

        if (!CompanySettings.getSingleton().isAsset()) {
            this.tableMovements.removeColumn(this.tableMovements.columns.find(col => "columnHeaderLoaded" === col.headingCell.id));
            this.tableMovements.removeColumn(this.tableMovements.columns.find(col => "columnHeaderEquipment" === col.headingCell.id));
            this.tableMovements.removeColumn(this.tableMovements.columns.find(col => "columnHeaderDrivers" === col.headingCell.id));
        }

        this.tableMovements.rowProps = {
            onDblClick: (event) => this.tableRowOnDblClick(event)
        };
        this.citystateStopOriginCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateStopDestCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateStopOriginCityId.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateStopDestCityId.textCity.forcedCase = ForcedCase.UPPER;

        this.panelMovementListContainer.rows[0].style.height = "100%"; // pretty nasty hack to make the vertical scroll bar behave
        this.setupEquipmentSearchPanel();
        PlanningUtil.showHideTrailerColumns(this.tableMovements);
    }

    private async lookupRow(updatedRow: ModelRow) {
        let id: string;
        if (updatedRow._modelPath === "lme/dispatch/orders") {
            id = updatedRow.get("id");
        }
        else {
            id = updatedRow.get("orders.id");
        }
        const results = await Model.search(this.tableMovements.dataSource.url, { "orders.id": id, _appending: true, _field_list: { layoutName: this.layoutName } });
        return results.getSingleModelRow();
    }

    /** This is an event handler for the beforeExecution event of sourceMovement. */
    sourceMovementBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH ) {
            const orderId = event.filter["orders.id"];
            if (LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"] === "Y"
                && !StringUtil.isEmptyString(orderId) && orderId?.startsWith("M") || orderId?.startsWith("m")) {
                event.filter["manifest_id"] = orderId;
                event.filter["orders.id"] = null;
            }
            if (ObjectUtil.isObject(event.filter) && Object.keys(event.filter).length == 0) {
                event.filter["id"] = "*";
            }
        }
    }

    private updateLoadedImage(tableRow: TableRow) {
        const loadedImage = tableRow.findComponentById("imageLoaded") as Image;
        if (!loadedImage) return;
        const loaded = tableRow.data.get("loaded") == "L";
        loadedImage.name = loaded  ? ImageName.circleCheckEmpty : ImageName.emptyMove;
        loadedImage.tooltip = loaded ? "Loaded" : "Empty";
    }

    /** This is an event handler for the onRowDisplay event of tableMovements. */
    tableMovementsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.updateLoadedImage(tableRow);
        const labelOrderId: Label = tableRow.findComponentById("labelOrderId") as Label;
        if (labelOrderId != null) {
            this.styleLinkLabel(labelOrderId);
            labelOrderId.addClickListener(() => {
                OrderTabView.navigateTo({key: tableRow.data.get("orders.id"), additionalProps: {showVoid: true}, newTab: true});
            });
        }
        const labelMovementId: Label = tableRow.findComponentById("labelMovementId") as Label;
        if (labelMovementId != null) {
            this.styleLinkLabel(labelMovementId);
            labelMovementId.addClickListener(() => {
                const movementId = tableRow.data.get("id");
                const orderId = !tableRow.data.isNull("orders.id") ? tableRow.data.get("orders.id"): "none";
                if (movementId) {
                   OrderTabView.navigateTo({key: movementId, orderId:orderId, additionalProps: {showVoid: true}, newTab: true});
                }
            });
        }
        const labelManifestId: Label = tableRow.findComponentById("labelManifestId") as Label;
        if (labelManifestId != null) {
            this.styleLinkLabel(labelManifestId);
            labelManifestId.addClickListener(() => {
                    Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + tableRow.data.get("manifest_id"), { newTab: true });
            });
        }
        if (tableRow.data.get("driver1.id")) {
            const labelDriver1: Label = tableRow.findComponentById("labelDriver1Name") as Label;
            if (labelDriver1)
                CommonTooltips.setTooltipContentLayout(labelDriver1, tableRow.data.get("driver1.id"), "lme/dispatch/asset/DriverQuickInfo");
        }
        if (tableRow.data.get("driver2.id")) {
            const labelDriver2: Label = tableRow.findComponentById("labelDriver2Name") as Label;
            if (labelDriver2)
                CommonTooltips.setTooltipContentLayout(labelDriver2, tableRow.data.get("driver2.id"), "lme/dispatch/asset/DriverQuickInfo");
        }
    }

    searchStopPopulateSelection(stopType: "origin" | "dest", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            this.sourceMovementList.searchRow.setValues({
                [`stop_${stopType}.address`]: selection.getString("address1", null),
                [`stop_${stopType}.address2`]: selection.getString("address2", null),
                [`stop_${stopType}.city_id`]: selection.getString("city_id", null),
                [`stop_${stopType}.city_name`]: selection.getString("city_name", null),
                [`stop_${stopType}.location_id`]: selection.getString("id", null),
                [`stop_${stopType}.location_name`]: selection.getString("name", null),
                [`stop_${stopType}.state`]: selection.getString("state", null),
                [`stop_${stopType}.zip_code`]: selection.getString("zip_code", null),
            });
        }
        this.searchStopSelection(stopType, targetPanel, event);
    }


    searchStopSelection(stopType: "origin" | "dest", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            if (!selection.isNull("state")) {
                this.sourceMovementList.searchRow.setLookupModelData(`stop_${stopType}.state`, { "id": selection.getString("state", null) });
            }
        }
        targetPanel.displayComponentData(this.sourceMovementList.searchRow, null, 0);
    }

    styleLinkLabel(label: Label) {
        label.addMouseEnterListener(event => {
            label.underlineWhenMouseOver(event.target, true);
            label.setClassIncluded(LabelStyles.link, true);
        });
        label.addMouseLeaveListener(event => {
            label.underlineWhenMouseOver(event.target, false);
            label.setClassIncluded(LabelStyles.link, false);
        });
    }

    /** This is an event handler for the onDblClick event of tableMovements. */
    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        const movementId = tableRow.data.get("id");
        const orderId = !tableRow.data.isNull("orders.id") ? tableRow.data.get("orders.id"): "none";
        if (this.tableMovements.detailLayout === "lme/dispatch/OrderTabView") {
            OrderTabView.slideUp({key: movementId, orderId: orderId, additionalProps: {showVoid: true}});
        }
        else {
            this.tableMovements.crudManager.showDetailFromClick(event);
        }
    }

    setupEquipmentSearchPanel() {
        const maxTrailers = getDispatchControlValue("maximum_trailers");
        this.textboxDolly1Id.visible = maxTrailers >= 2;
        this.textboxTrailer2Id.visible = maxTrailers >= 2;
        this.textboxDolly2Id.visible = maxTrailers >= 3;
        this.textboxTrailer3Id.visible = maxTrailers >= 3;
        this.textboxTrailerId?.setProps({ caption: maxTrailers > 1 ? "Trailer 1" : "Trailer" });
        this.textboxDolly1Id?.setProps({ caption: maxTrailers > 2 ? "Dolly 1" : "Dolly" });
        if (getDispatchControlValue("maximum_drivers") >= 2) {
            this.textboxDriver2Name.visible = true;
            this.textboxDriver1Name.caption = "Driver 1 Name";
        }    
    }
}
